import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BASE = "https://dailydish.in/api/admin";
const HUB_BASE = "https://dailydish.in/api/Hub";

// ---- Validated dataviz palette (dataviz skill / references/palette.md) ----
// Chrome (chart surfaces/ink) stays neutral; data marks use the validated
// sequential/categorical hues below — never the app's green UI accent, which
// hasn't been run through the CVD/contrast validator.
const INK_PRIMARY = "#0b0b0b";
const INK_SECONDARY = "#52514e";
const INK_MUTED = "#898781";
const GRID = "#e1e0d9";
const SEQ_BLUE = "#2a78d6"; // sequential context 1 (magnitude)
const SEQ_AQUA = "#1baf7a"; // sequential context 2 (magnitude, shown alongside the first)
const STATUS_GOOD = "#0ca30c";

// Fixed categorical slot per channel — color follows the entity, never rank,
// so it can never be reassigned by a filter or sort.
const CHANNEL_COLORS = {
  flyer: "#2a78d6", // slot 1 blue
  standie: "#1baf7a", // slot 2 aqua
  lift_banner: "#eda100", // slot 3 yellow
  whatsapp: "#008300", // slot 4 green
  door_tag: "#4a3aa7", // slot 5 violet
  alpine: "#e34948", // slot 6 red
  hiranandani: "#e87ba4", // slot 7 magenta
  organic: "#eb6834", // slot 8 orange
};
const OTHER_COLOR = INK_MUTED; // anything past 8 slots (e.g. "refer") folds into "Other"

const CHANNEL_LABELS = {
  flyer: "Flyer",
  standie: "Standie",
  lift_banner: "Lift Banner",
  whatsapp: "WhatsApp",
  door_tag: "Door Tag",
  alpine: "Alpine (Apartment)",
  hiranandani: "Hiranandani (Apartment)",
  organic: "Organic",
  refer: "Referral",
};

const channelColor = (ch) => CHANNEL_COLORS[ch] || OTHER_COLOR;
const channelLabel = (ch) => CHANNEL_LABELS[ch] || ch;

const formatCompact = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: "1.5px solid #ddd",
  fontSize: "14px",
  fontFamily: "Inter",
};

const cardStyle = {
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid #e8f0d8",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
};

const StatTile = ({ label, value, sub, accent }) => (
  <div
    style={{
      ...cardStyle,
      padding: "16px 20px",
      flex: "1 1 160px",
      minWidth: "160px",
    }}
  >
    <div
      style={{
        fontSize: "26px",
        fontWeight: 700,
        color: INK_PRIMARY,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: "12.5px", color: INK_SECONDARY, marginTop: "4px" }}>
      {label}
    </div>
    {sub && (
      <div
        style={{
          fontSize: "11.5px",
          color: accent || INK_MUTED,
          marginTop: "4px",
          fontWeight: 600,
        }}
      >
        {sub}
      </div>
    )}
  </div>
);

const tooltipBase = {
  backgroundColor: "#fff",
  titleColor: INK_PRIMARY,
  bodyColor: INK_SECONDARY,
  borderColor: GRID,
  borderWidth: 1,
  padding: 10,
  displayColors: true,
};

const CampaignDashboard = () => {
  const [hubs, setHubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Date filter — narrows the whole view to a sub-range within the campaign's
  // own date range. Defaults to the full campaign range whenever a new
  // campaign's data arrives.
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  // Create-campaign form state
  const [name, setName] = useState("");
  const [hubId, setHubId] = useState("");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchHubs = async () => {
    try {
      const res = await axios.get(`${HUB_BASE}/hubs`);
      setHubs(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${BASE}/campaigns`);
      if (res.data.success) setCampaigns(res.data.campaigns);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCampaignStats = async (campaignId) => {
    if (!campaignId) {
      setStatsData(null);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${BASE}/campaigns/${campaignId}/stats`);
      if (res.data.success) setStatsData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHubs();
    fetchCampaigns();
  }, []);

  // Auto-default: prefer the most-recently-created campaign that's currently
  // active (today within its date range); otherwise fall back to the most
  // recently created one overall. No manual selection needed to see something.
  useEffect(() => {
    if (campaigns.length === 0 || selectedCampaignId) return;
    const now = new Date();
    const active = campaigns.find((c) => {
      const start = new Date(c.startDate);
      const end = c.endDate ? new Date(c.endDate) : null;
      return start <= now && (!end || now <= end);
    });
    setSelectedCampaignId((active || campaigns[0])._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  useEffect(() => {
    fetchCampaignStats(selectedCampaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId]);

  // Reset the date filter to the campaign's full range whenever a different
  // campaign's data lands.
  useEffect(() => {
    if (!statsData?.campaign) return;
    const c = statsData.campaign;
    setFilterStart(new Date(c.startDate).toISOString().split("T")[0]);
    setFilterEnd(
      (c.endDate ? new Date(c.endDate) : new Date())
        .toISOString()
        .split("T")[0],
    );
  }, [statsData?.campaign]);

  const handleCreateCampaign = async () => {
    if (!name || !hubId || !startDate) {
      alert("Name, hub and start date are required.");
      return;
    }
    try {
      setCreating(true);
      const hub = hubs.find((h) => h._id === hubId);
      const res = await axios.post(`${BASE}/campaigns`, {
        name,
        hubId,
        hubName: hub?.hubName || "",
        startDate,
        endDate: endDate || null,
      });
      if (res.data.success) {
        setName("");
        setHubId("");
        setEndDate("");
        setShowCreateForm(false);
        await fetchCampaigns();
        setSelectedCampaignId(res.data.campaign._id);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const campaign = statsData?.campaign;

  // Every downstream number (KPIs, charts, table) is derived from this
  // filtered slice, so the date filter scopes the whole page consistently.
  const rows = useMemo(() => {
    const allRows = statsData?.rows || [];
    if (!filterStart && !filterEnd) return allRows;
    return allRows.filter(
      (r) =>
        (!filterStart || r.date >= filterStart) &&
        (!filterEnd || r.date <= filterEnd),
    );
  }, [statsData, filterStart, filterEnd]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          visits: acc.visits + r.visits,
          registered: acc.registered + r.registered,
          ordered: acc.ordered + r.ordered,
        }),
        { visits: 0, registered: 0, ordered: 0 },
      ),
    [rows],
  );
  const regRate =
    totals.visits > 0
      ? ((totals.registered / totals.visits) * 100).toFixed(1)
      : null;
  const orderRate =
    totals.registered > 0
      ? ((totals.ordered / totals.registered) * 100).toFixed(1)
      : null;

  // Per-channel totals within the filtered date range — the channel comparison view.
  const channelTotals = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.channel])
        map[r.channel] = {
          channel: r.channel,
          visits: 0,
          registered: 0,
          ordered: 0,
        };
      map[r.channel].visits += r.visits;
      map[r.channel].registered += r.registered;
      map[r.channel].ordered += r.ordered;
    });
    return Object.values(map)
      .map((c) => ({
        ...c,
        regRate:
          c.visits > 0
            ? Number(((c.registered / c.visits) * 100).toFixed(1))
            : null,
        orderRate:
          c.registered > 0
            ? Number(((c.ordered / c.registered) * 100).toFixed(1))
            : null,
      }))
      .sort((a, b) => b.visits - a.visits);
  }, [rows]);

  // Two distinct leaders — the channel with the most scans isn't necessarily
  // the one that converts best, so both get their own callout.
  const topByScans = channelTotals[0];
  const topByConversion = useMemo(() => {
    const withConversion = channelTotals.filter(
      (c) => c.visits > 0 && c.regRate !== null,
    );
    if (withConversion.length === 0) return null;
    return [...withConversion].sort((a, b) => b.regRate - a.regRate)[0];
  }, [channelTotals]);

  const isActive =
    campaign &&
    new Date(campaign.startDate) <= new Date() &&
    (!campaign.endDate || new Date() <= new Date(campaign.endDate));

  const scansBarData = {
    labels: channelTotals.map((c) => channelLabel(c.channel)),
    datasets: [
      {
        label: "Scans",
        data: channelTotals.map((c) => c.visits),
        backgroundColor: SEQ_BLUE,
        borderRadius: 4,
        barThickness: 20,
        maxBarThickness: 24,
      },
    ],
  };
  const conversionBarData = {
    labels: channelTotals.map((c) => channelLabel(c.channel)),
    datasets: [
      {
        label: "Conversion %",
        data: channelTotals.map((c) => c.regRate ?? 0),
        backgroundColor: SEQ_AQUA,
        borderRadius: 4,
        barThickness: 20,
        maxBarThickness: 24,
      },
    ],
  };
  const horizontalBarOptions = (unit) => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipBase,
        callbacks: { label: (ctx) => `${ctx.parsed.x}${unit}` },
      },
    },
    scales: {
      x: {
        grid: { color: GRID, drawTicks: false },
        ticks: { color: INK_MUTED, font: { size: 11 } },
        border: { display: false },
        beginAtZero: true,
      },
      y: {
        grid: { display: false },
        ticks: { color: INK_SECONDARY, font: { size: 12 } },
        border: { display: false },
      },
    },
  });

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Inter, sans-serif",
        background: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #6B8E23 0%, #4a6e12 100%)",
          borderRadius: "16px",
          padding: "24px 28px",
          color: "#fff",
          marginBottom: "20px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700" }}>
          🎯 Hub Campaign Analytics
        </h2>
        <p style={{ margin: "4px 0 0", opacity: 0.85, fontSize: "14px" }}>
          Scans → registrations → orders, compared across marketing channels for
          a hub campaign.
        </p>
      </div>

      {/* Filter row: campaign switcher + new-campaign toggle. One row, scopes everything below. */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <select
          value={selectedCampaignId}
          onChange={(e) => setSelectedCampaignId(e.target.value)}
          style={{ ...inputStyle, minWidth: "300px" }}
        >
          <option value="">Select a campaign</option>
          {campaigns.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name} · {c.hubName} · from{" "}
              {new Date(c.startDate).toLocaleDateString("en-IN")}
              {c.endDate
                ? ` to ${new Date(c.endDate).toLocaleDateString("en-IN")}`
                : " (ongoing)"}
            </option>
          ))}
        </select>
        <button
          onClick={() => setShowCreateForm((v) => !v)}
          style={{
            padding: "10px 18px",
            borderRadius: "10px",
            border: "1.5px solid #6B8E23",
            background: showCreateForm ? "#6B8E23" : "#fff",
            color: showCreateForm ? "#fff" : "#6B8E23",
            fontWeight: "700",
            cursor: "pointer",
          }}
        >
          {showCreateForm ? "✕ Cancel" : "+ New Campaign"}
        </button>
      </div>

      {showCreateForm && (
        <div style={{ ...cardStyle, padding: "20px", marginBottom: "20px" }}>
          <h3
            style={{
              margin: "0 0 14px",
              fontSize: "15px",
              color: "#3a5a0e",
              fontWeight: "700",
            }}
          >
            Create Campaign
          </h3>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                Campaign Name
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. July Flyer Drive"
                style={{ ...inputStyle, minWidth: "220px" }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                Hub
              </div>
              <select
                value={hubId}
                onChange={(e) => setHubId(e.target.value)}
                style={{ ...inputStyle, minWidth: "220px" }}
              >
                <option value="">Select a hub</option>
                {hubs.map((h) => (
                  <option key={h._id} value={h._id}>
                    {h.hubName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                Start Date
              </div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                End Date (optional)
              </div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <button
              onClick={handleCreateCampaign}
              disabled={creating}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: "#6B8E23",
                color: "#fff",
                fontWeight: "700",
                cursor: creating ? "not-allowed" : "pointer",
              }}
            >
              {creating ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </div>
      )}

      {!selectedCampaignId ? (
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            padding: "60px",
            color: "#888",
          }}
        >
          {campaigns.length === 0
            ? "No campaigns yet — create one to start tracking a hub's performance."
            : "Select a campaign above to see its analysis."}
        </div>
      ) : (
        <>
          {/* Campaign meta strip */}
          {campaign && (
            <div
              style={{
                ...cardStyle,
                padding: "14px 20px",
                marginBottom: "20px",
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "15px",
                  color: INK_PRIMARY,
                }}
              >
                {campaign.name}
              </div>
              <div style={{ fontSize: "13px", color: INK_SECONDARY }}>
                🏬 {campaign.hubName}
              </div>
              <div style={{ fontSize: "13px", color: INK_SECONDARY }}>
                📅 {new Date(campaign.startDate).toLocaleDateString("en-IN")}
                {" → "}
                {campaign.endDate
                  ? new Date(campaign.endDate).toLocaleDateString("en-IN")
                  : "ongoing"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: isActive ? STATUS_GOOD : INK_MUTED,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span style={{ fontSize: "9px" }}>●</span>{" "}
                {isActive ? "Active" : "Ended"}
              </div>
            </div>
          )}

          {/* Date filter — one row, scopes every KPI/chart/table below it */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "flex-end",
              marginBottom: "20px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                From
              </div>
              <input
                type="date"
                value={filterStart}
                min={
                  campaign
                    ? new Date(campaign.startDate).toISOString().split("T")[0]
                    : undefined
                }
                max={filterEnd || undefined}
                onChange={(e) => setFilterStart(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                To
              </div>
              <input
                type="date"
                value={filterEnd}
                min={filterStart || undefined}
                max={
                  campaign && campaign.endDate
                    ? new Date(campaign.endDate).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0]
                }
                onChange={(e) => setFilterEnd(e.target.value)}
                style={inputStyle}
              />
            </div>
            {campaign && (
              <button
                onClick={() => {
                  setFilterStart(
                    new Date(campaign.startDate).toISOString().split("T")[0],
                  );
                  setFilterEnd(
                    (campaign.endDate ? new Date(campaign.endDate) : new Date())
                      .toISOString()
                      .split("T")[0],
                  );
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: "10px",
                  border: "1.5px solid #ddd",
                  background: "#fff",
                  color: INK_SECONDARY,
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Full Campaign Range
              </button>
            )}
          </div>

          {/* KPI row */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            <StatTile
              label="Total Scans"
              value={formatCompact(totals.visits)}
            />
            <StatTile
              label="Registered"
              value={formatCompact(totals.registered)}
              sub={regRate !== null ? `${regRate}% of scans` : "—"}
              accent="#3a5a0e"
            />
            <StatTile
              label="Ordered"
              value={formatCompact(totals.ordered)}
              sub={orderRate !== null ? `${orderRate}% of registered` : "—"}
              accent="#3a5a0e"
            />
            <StatTile
              label="🏆 Most Scans"
              value={topByScans ? channelLabel(topByScans.channel) : "—"}
              sub={topByScans ? `${topByScans.visits} scans` : undefined}
              accent={topByScans ? channelColor(topByScans.channel) : undefined}
            />
            <StatTile
              label="🎯 Highest Conversion"
              value={
                topByConversion ? channelLabel(topByConversion.channel) : "—"
              }
              sub={
                topByConversion
                  ? `${topByConversion.regRate}% of ${topByConversion.visits} scans`
                  : undefined
              }
              accent={
                topByConversion
                  ? channelColor(topByConversion.channel)
                  : undefined
              }
            />
          </div>

          {loading ? (
            <div
              style={{
                ...cardStyle,
                textAlign: "center",
                padding: "60px",
                color: "#888",
              }}
            >
              Loading...
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{
                ...cardStyle,
                textAlign: "center",
                padding: "60px",
                color: "#888",
              }}
            >
              No scans recorded for this campaign's date range yet.
            </div>
          ) : (
            <>
              {/* Channel comparison — two sequential-hue rankings, never mixed on one axis */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div style={{ ...cardStyle, padding: "18px" }}>
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "14px",
                      color: INK_PRIMARY,
                      fontWeight: 700,
                    }}
                  >
                    Scans by Channel
                  </h3>
                  <div
                    style={{
                      height: `${Math.max(120, channelTotals.length * 42 + 40)}px`,
                    }}
                  >
                    <Bar
                      data={scansBarData}
                      options={horizontalBarOptions("")}
                    />
                  </div>
                </div>
                <div style={{ ...cardStyle, padding: "18px" }}>
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "14px",
                      color: INK_PRIMARY,
                      fontWeight: 700,
                    }}
                  >
                    Conversion Rate by Channel
                  </h3>
                  <div
                    style={{
                      height: `${Math.max(120, channelTotals.length * 42 + 40)}px`,
                    }}
                  >
                    <Bar
                      data={conversionBarData}
                      options={horizontalBarOptions("%")}
                    />
                  </div>
                </div>
              </div>

              {/* Full detail table — the accessible twin of every chart above */}
              <div style={{ ...cardStyle, overflow: "hidden" }}>
                <div
                  style={{
                    padding: "16px 20px 4px",
                    fontSize: "14px",
                    fontWeight: 700,
                    color: INK_PRIMARY,
                  }}
                >
                  Channel Summary
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${GRID}` }}>
                        {[
                          "Channel",
                          "Scans",
                          "Registered",
                          "Ordered",
                          "Reg. Rate",
                          "Order Rate",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "12px 20px",
                              textAlign: "left",
                              fontWeight: 700,
                              color: INK_MUTED,
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                      {channelTotals.map((c, idx) => (
                        <tr
                          key={c.channel}
                          style={{
                            borderBottom: `1px solid ${GRID}`,
                            background: idx % 2 === 0 ? "#fff" : "#fafcf7",
                          }}
                        >
                          <td style={{ padding: "12px 20px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: "9px",
                                height: "9px",
                                borderRadius: "50%",
                                background: channelColor(c.channel),
                                marginRight: "8px",
                              }}
                            />
                            <span
                              style={{ fontWeight: 600, color: INK_PRIMARY }}
                            >
                              {channelLabel(c.channel)}
                            </span>
                          </td>
                          <td style={{ padding: "12px 20px" }}>{c.visits}</td>
                          <td style={{ padding: "12px 20px" }}>
                            {c.registered}
                          </td>
                          <td style={{ padding: "12px 20px" }}>{c.ordered}</td>
                          <td style={{ padding: "12px 20px" }}>
                            {c.regRate === null ? "—" : `${c.regRate}%`}
                          </td>
                          <td style={{ padding: "12px 20px" }}>
                            {c.orderRate === null ? "—" : `${c.orderRate}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          borderTop: `2px solid ${GRID}`,
                          fontWeight: 700,
                        }}
                      >
                        <td style={{ padding: "12px 20px" }}>Total</td>
                        <td style={{ padding: "12px 20px" }}>
                          {totals.visits}
                        </td>
                        <td style={{ padding: "12px 20px" }}>
                          {totals.registered}
                        </td>
                        <td style={{ padding: "12px 20px" }}>
                          {totals.ordered}
                        </td>
                        <td style={{ padding: "12px 20px" }}>
                          {regRate === null ? "—" : `${regRate}%`}
                        </td>
                        <td style={{ padding: "12px 20px" }}>
                          {orderRate === null ? "—" : `${orderRate}%`}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignDashboard;
